/**
 * Phase 12 — Notification settings sub-page.
 *
 * Merges two surfaces into one:
 *   1. Approval channels (multi-platform alert config — Slack/Teams/GChat/Email).
 *      Ported from `src/pages/agency-portal/Notifications.tsx`.
 *   2. Email preferences (legacy newsletter / custom-message section).
 *      Untouched — preserved verbatim from the previous version of this file.
 *
 * Approval channels are scoped to the active shop (`profile.active_shop_slug`).
 * If no active shop is selected we show a banner. Connect / Disconnect / Test
 * buttons gate via `<RoleGate capability="notifications.configure">`. Read-only
 * roles still see the status badges.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import StatusPill from '../../../ui/statusPill/StatusPill';
import { setMessage } from '../../../store/reducers/appSlice';
// Reuse the shared confirm-popup styles (`.teamPopup`) used by Team / Control
// Center so the disconnect confirmation matches the rest of the portal.
import '../../../styles/pages/team/team.scss';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import store from '../../../store/store';
import { asyncHandler } from '../../../lib/api/async-handler';
import { applicationWWWXRLEncodedBodyBuilder } from '../../../lib/utils/body-builder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import { useTranslation } from 'react-i18next';
import {
  invalidEmails,
  isValidEmail,
  splitEmails,
} from '../../../lib/utils/email';
import { setUserCompanySettings } from '../../../store/reducers/userSlice';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { SHOP_API } from '../../../lib/constants/central-api';
import { submitLogToBugsnag } from '../../../lib/api/log';
import NormalInput from '../../../ui/inputs/NormalInput';
import { HOME_ROUTE } from '../../../lib/constants/route';
import {
  ChannelType,
  NotificationChannel,
  TeamsLinkCode,
  connectGoogleChat,
  disconnectChannel,
  generateTeamsCode,
  getInstallUrl,
  installManual,
  listChannels,
  testChannel,
} from '../../../lib/api/notifications/notifications-api';
import { RoleGate, useEmployeeRole, can } from '../../../lib/rbac';

// ---------- approval-channel constants ----------

const POLL_INTERVAL_MS = 3000;
const POLL_DURATION_MS = 60_000;
// Teams link completes server-side; poll the channel list every ~5s until it
// lands or the connection code expires.
const TEAMS_POLL_INTERVAL_MS = 5000;

interface PlatformDef {
  type: ChannelType;
  label: string;
  manual: boolean;
  description: string;
}

const PLATFORMS: PlatformDef[] = [
  {
    type: 'slack',
    label: 'Slack',
    manual: false,
    description:
      'Real-time alerts in any Slack channel. One-click OAuth install.',
  },
  {
    type: 'teams',
    label: 'Microsoft Teams',
    manual: true,
    description:
      'Add the ADUP bot to a Teams channel and link it with a one-time connection code.',
  },
  {
    type: 'gchat',
    label: 'Google Chat',
    manual: true,
    description:
      'Add the ADUP app to a Google Chat space, then paste its Space ID to connect.',
  },
  {
    type: 'email',
    label: 'Email (Resend)',
    manual: true,
    description:
      'Email proposal alerts to a custom recipient list via your Resend account.',
  },
];

/**
 * Microsoft Teams now uses connection-code linking (no paste-in credentials).
 * `teamsLink` holds the active code/countdown while the agency completes the
 * link inside Teams; `null` means no code has been generated yet.
 */
interface TeamsLinkState {
  code: string;
  expiresAt: number; // epoch ms
  instructions: string;
}

/**
 * Google Chat now uses a single shared ADUP service-account bot — the agency
 * only supplies the Space ID. `spaceId` is the input; `unreachable` flags the
 * 502 "bot can't reach that space" case so we can show an inline hint.
 */
interface GChatForm {
  spaceId: string;
}
const emptyGChatForm: GChatForm = {
  spaceId: '',
};

interface EmailForm {
  resend_api_key: string;
  sender_email: string;
  sender_name: string;
  recipients: string[];
  recipientDraft: string;
}
const emptyEmailForm: EmailForm = {
  resend_api_key: '',
  sender_email: '',
  sender_name: '',
  recipients: [],
  recipientDraft: '',
};

function findChannel(
  list: NotificationChannel[],
  type: ChannelType
): NotificationChannel | undefined {
  return list.find((c) => c.channel_type === type && c.is_active);
}

/** Format the remaining time on a Teams connection code as `m:ss`. */
function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.ceil(remainingMs / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function externalLabel(c: NotificationChannel): string {
  if (c.external_id) return c.external_id;
  const cfg = (c.config || {}) as Record<string, unknown>;
  const candidates = [
    'team_name',
    'workspace_name',
    'space_name',
    'sender_email',
    'channel_name',
    'tenant_id',
    'space_id',
  ];
  for (const key of candidates) {
    const v = cfg[key];
    if (typeof v === 'string' && v) return v;
  }
  return `${c.channel_type} #${c.id}`;
}

const SettingsNotifications = () => {
  const location = useLocation();
  const activeLink = location.pathname;

  const { t } = useTranslation();

  // redux state
  const newsLetterText = useSelector(
    (state: RootState) => state.user.merchant.config.monthly_newsletter_text
  );
  const accessToken = store.getState().auth.authorization.accessToken;
  const isWhiteLabel = store.getState().UI?.isWhiteLabel;
  const slug = store.getState().user?.company?.shop_slug;
  // Phase 12 — approval-channel section scopes to the user's currently active
  // shop. Read live from the store rather than capturing once at mount so
  // shop-switcher updates propagate.
  const activeShopSlug = useSelector(
    (state: RootState) => state.user?.profile?.active_shop_slug
  );
  // The notifications-api fetches use the agency Bearer token. If the user
  // never bridged into the agency portal (no `auth.agency.accessToken`),
  // skip the network call — surfacing a 401 banner here would be noise.
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  // Guards use the boolean, but effects must key on the token VALUE: the
  // admin "login as seller" handoff swaps the agency session mid-page, and a
  // boolean dep would skip the refetch and keep stale wrong-org data.
  const hasAgencyToken = Boolean(agencyToken);

  // RBAC
  const role = useEmployeeRole();
  const canConfigureChannels = can(role, 'notifications.configure');

  // ---------- legacy email-preferences local state ----------
  const [sendNewsLetter, setSendNewsLetter] = useState<boolean>(false);
  const [isShowCustomMessage, setIsShowCustomMessage] =
    useState<boolean>(false);
  const [customMessage, setCustomMessage] = useState<string>('');
  const [loading, setLoading] = useState('pending');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ---------- approval-channel local state ----------
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  // Journey C — toasts go through the shared Redux `setMessage` banner (same as
  // the rest of the agency portal) instead of a local MUI Snackbar. The four
  // severities map 1:1 to the global `Message` component.
  const showToast = (
    severity: 'success' | 'info' | 'warning' | 'error',
    text: string
  ) => store.dispatch(setMessage({ messageType: severity, message: text }));

  const [openPanels, setOpenPanels] = useState<Record<ChannelType, boolean>>({
    slack: false,
    teams: false,
    gchat: false,
    email: false,
    webhook: false,
    discord: false,
  });
  const [connectingType, setConnectingType] = useState<ChannelType | null>(
    null
  );
  const [submittingType, setSubmittingType] = useState<ChannelType | null>(
    null
  );
  const [testingId, setTestingId] = useState<number | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<{
    open: boolean;
    channel?: NotificationChannel;
  }>({ open: false });
  const [disconnecting, setDisconnecting] = useState(false);

  const [gchatForm, setGchatForm] = useState<GChatForm>(emptyGChatForm);
  // GChat 502 — bot can't reach the pasted space (it was never added to it).
  const [gchatUnreachable, setGchatUnreachable] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailForm>(emptyEmailForm);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  // ---------- Teams connection-code linking ----------
  // Active code + countdown while the agency completes the link inside Teams.
  const [teamsLink, setTeamsLink] = useState<TeamsLinkState | null>(null);
  // 1s ticker that drives the countdown display (only runs while a code is up).
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const [teamsCopied, setTeamsCopied] = useState(false);

  let sellerToken: string;
  if (isWhiteLabel) {
    sellerToken = accessToken?.currentWhiteLabelSellerToken;
  } else {
    sellerToken = accessToken?.sellerToken;
  }

  // ---------- Slack OAuth polling ----------
  const slackPollRef = useRef<{
    timerId: number | null;
    stopAt: number;
  }>({ timerId: null, stopAt: 0 });

  const stopSlackPoll = useCallback(() => {
    if (slackPollRef.current.timerId !== null) {
      window.clearTimeout(slackPollRef.current.timerId);
      slackPollRef.current.timerId = null;
    }
  }, []);

  useEffect(() => stopSlackPoll, [stopSlackPoll]);

  // ---------- Teams connection-code polling ----------
  // The agency completes the link inside Teams, so the channel appears
  // server-side asynchronously. After we show a code we poll `listChannels`
  // every TEAMS_POLL_INTERVAL_MS until a `teams` channel lands or the code
  // expires. `stopAt` mirrors the code's `expires_at`.
  const teamsPollRef = useRef<{
    timerId: number | null;
    stopAt: number;
  }>({ timerId: null, stopAt: 0 });

  const stopTeamsPoll = useCallback(() => {
    if (teamsPollRef.current.timerId !== null) {
      window.clearTimeout(teamsPollRef.current.timerId);
      teamsPollRef.current.timerId = null;
    }
  }, []);

  useEffect(() => stopTeamsPoll, [stopTeamsPoll]);

  // Drive the Teams countdown — a 1s ticker that only runs while a code is up.
  useEffect(() => {
    if (!teamsLink) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [teamsLink]);

  // When the code expires, stop polling and clear it so the card resets to the
  // "Generate connection code" state.
  useEffect(() => {
    if (teamsLink && nowMs >= teamsLink.expiresAt) {
      stopTeamsPoll();
      setTeamsLink(null);
      setTeamsCopied(false);
    }
  }, [teamsLink, nowMs, stopTeamsPoll]);

  // ---------- approval-channel data loaders ----------
  const loadChannels = useCallback(
    async (shopSlug: string): Promise<NotificationChannel[]> => {
      if (!shopSlug) return [];
      setChannelsLoading(true);
      setChannelsError(null);
      try {
        const list = await listChannels(shopSlug);
        setChannels(list);
        return list;
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error('[SettingsNotifications] load channels failed', err);
        const e = err as { status?: number; message?: string };
        if (e?.status === 403) {
          setChannelsError(
            'You do not have permission to view notification channels for this shop.'
          );
        } else {
          setChannelsError(
            e?.message || 'Could not load notification channels.'
          );
        }
        setChannels([]);
        return [];
      } finally {
        setChannelsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (activeShopSlug && hasAgencyToken) {
      loadChannels(activeShopSlug);
    } else {
      setChannels([]);
    }
    // Reset forms whenever the shop changes.
    setGchatForm(emptyGChatForm);
    setGchatUnreachable(false);
    setEmailForm(emptyEmailForm);
    setTeamsLink(null);
    setTeamsCopied(false);
    setOpenPanels({
      slack: false,
      teams: false,
      gchat: false,
      email: false,
      webhook: false,
      discord: false,
    });
    stopSlackPoll();
    stopTeamsPoll();
  }, [
    activeShopSlug,
    agencyToken,
    loadChannels,
    stopSlackPoll,
    stopTeamsPoll,
  ]);

  // ---------- legacy email-preferences fetch (kept intact) ----------
  useEffect(() => {
    if (slug && slug !== '') {
      const response = getNotificationsSettings();
      response
        .then((result) => {
          setSendNewsLetter(
            result?.send_monthly_newsletter === 'true' ? true : false
          );
          setIsShowCustomMessage(
            result?.is_show_custom_message === 'true' ? true : false
          );
          setCustomMessage(
            result?.custom_message ? result?.custom_message : ''
          );
          setLoading('success');
        })
        .catch((err) => {
          console.error(err);
          setLoading('error');
          submitLogToBugsnag('error', `${err}`);
        });
    }
  }, [slug]);

  /* ----------------------------API CALL START---------------------------- */
  async function getNotificationsSettings() {
    try {
      const requestInfo: RequestInfo = {
        url: SHOP_API + `/${slug}/settings?filter[group]=notifications`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'GET_NOTIFICATIONS_SETTINGS';
      const customMsg = 'Notifications Settings fetched Successfully.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false
      );

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        let data: any = {};
        response?.data?.forEach((item: any) => {
          data[item.key] = item.value === null ? '' : item.value;
        });
        return data;
      }
    } catch (exception) {
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  async function onSubmitHandler(e: any) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const sendingData = {
        group: 'notifications',
        send_monthly_newsletter: sendNewsLetter,
        is_show_custom_message: isShowCustomMessage,
        custom_message: customMessage,
      };

      const requestInfo: RequestInfo = {
        url: SHOP_API + `/${slug}/settings`,
        method: 'PUT',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'UPDATE_NOTIFICATIONS_SETTINGS';
      const customMsg = t('SettingsUpdatedSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);
      setIsSubmitting(false);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        store.dispatch(setUserCompanySettings(response?.result));
        return response.result;
      }
    } catch (exception) {
      console.log('Error during authentication request');
      setIsSubmitting(false);
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  /* ----------------------------API CALL END---------------------------- */

  // ---------- approval-channel handlers ----------
  const startSlackPolling = useCallback(() => {
    if (!activeShopSlug) return;
    stopSlackPoll();
    slackPollRef.current.stopAt = Date.now() + POLL_DURATION_MS;
    const tick = async () => {
      const list = await loadChannels(activeShopSlug);
      const hasSlack = !!findChannel(list, 'slack');
      if (hasSlack) {
        stopSlackPoll();
        showToast('success', 'Slack connected.');
        return;
      }
      if (Date.now() >= slackPollRef.current.stopAt) {
        stopSlackPoll();
        return;
      }
      slackPollRef.current.timerId = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    slackPollRef.current.timerId = window.setTimeout(tick, POLL_INTERVAL_MS);
  }, [activeShopSlug, loadChannels, stopSlackPoll]);

  // Poll the channel list until a `teams` channel lands (the agency completes
  // the link inside Teams) or the connection code expires (`stopAt`).
  const startTeamsPolling = useCallback(
    (stopAt: number) => {
      if (!activeShopSlug) return;
      stopTeamsPoll();
      teamsPollRef.current.stopAt = stopAt;
      const tick = async () => {
        const list = await loadChannels(activeShopSlug);
        const hasTeams = !!findChannel(list, 'teams');
        if (hasTeams) {
          stopTeamsPoll();
          setTeamsLink(null);
          setTeamsCopied(false);
          showToast('success', 'Microsoft Teams connected.');
          return;
        }
        if (Date.now() >= teamsPollRef.current.stopAt) {
          stopTeamsPoll();
          return;
        }
        teamsPollRef.current.timerId = window.setTimeout(
          tick,
          TEAMS_POLL_INTERVAL_MS
        );
      };
      teamsPollRef.current.timerId = window.setTimeout(
        tick,
        TEAMS_POLL_INTERVAL_MS
      );
    },
    [activeShopSlug, loadChannels, stopTeamsPoll]
  );

  // Teams "Connect" = request a one-time connection code, show it + a countdown,
  // and start polling for the async link to complete inside Teams.
  const handleGenerateTeamsCode = async () => {
    if (!activeShopSlug || !canConfigureChannels) return;
    setConnectingType('teams');
    setChannelsError(null);
    setTeamsCopied(false);
    try {
      const res: TeamsLinkCode = await generateTeamsCode(activeShopSlug);
      const expiresAt = Date.parse(res.expires_at);
      // Fall back to ttl_seconds if expires_at isn't a parseable date.
      const safeExpiresAt = Number.isNaN(expiresAt)
        ? Date.now() + (res.ttl_seconds || 0) * 1000
        : expiresAt;
      setNowMs(Date.now());
      setTeamsLink({
        code: res.code,
        expiresAt: safeExpiresAt,
        instructions: res.instructions,
      });
      startTeamsPolling(safeExpiresAt);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] teams link-code failed', err);
      const e = err as { message?: string };
      setChannelsError(
        e?.message || 'Failed to generate a Teams connection code.'
      );
    } finally {
      setConnectingType(null);
    }
  };

  const handleCopyTeamsCode = () => {
    if (!teamsLink) return;
    try {
      navigator.clipboard.writeText(teamsLink.code);
    } catch {
      // Clipboard may be unavailable (insecure context) — the code is still
      // visible for manual copy, so swallow.
    }
    setTeamsCopied(true);
    window.setTimeout(() => setTeamsCopied(false), 1500);
  };

  const handleConnectClick = async (platform: PlatformDef) => {
    if (!activeShopSlug || !canConfigureChannels) return;
    // Google Chat connects directly via `gchat/manual` (no install-url step),
    // so just reveal the inline Space ID form. Teams is handled by its own
    // code-generation action and never reaches here.
    if (platform.type === 'gchat') {
      setChannelsError(null);
      setGchatUnreachable(false);
      setOpenPanels((p) => ({ ...p, gchat: true }));
      return;
    }
    setConnectingType(platform.type);
    setChannelsError(null);
    try {
      const resp = await getInstallUrl(activeShopSlug, platform.type);
      if (!platform.manual && resp.install_url) {
        window.open(resp.install_url, '_blank', 'noopener,noreferrer');
        startSlackPolling();
        showToast(
          'info',
          'Opened Slack install in a new tab. We are watching for the connection…'
        );
      } else {
        setOpenPanels((p) => ({ ...p, [platform.type]: true }));
      }
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] install-url failed', err);
      const e = err as { message?: string };
      setChannelsError(
        e?.message || `Failed to start ${platform.label} install.`
      );
    } finally {
      setConnectingType(null);
    }
  };

  const handleSubmitGChat = async () => {
    if (!activeShopSlug || !canConfigureChannels) return;
    if (!gchatForm.spaceId.trim()) {
      setChannelsError('A Google Chat Space ID is required.');
      return;
    }
    setSubmittingType('gchat');
    setChannelsError(null);
    setGchatUnreachable(false);
    try {
      await connectGoogleChat(activeShopSlug, gchatForm.spaceId.trim());
      setGchatForm(emptyGChatForm);
      setOpenPanels((p) => ({ ...p, gchat: false }));
      showToast('success', 'Google Chat connected.');
      await loadChannels(activeShopSlug);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] gchat connect failed', err);
      const e = err as {
        status?: number;
        message?: string;
        error?: string;
        validation?: Record<string, string[]>;
      };
      // 502 → the shared ADUP bot can't reach that space (it was never added
      // to it). Surface the inline "add the ADUP app" hint instead of a raw
      // gateway error string.
      const gatewayCodes = [
        'gateway_install_failed',
        'gchat_verification_failed',
        'gateway_unreachable',
      ];
      if (
        e?.status === 502 ||
        (e?.error && gatewayCodes.includes(e.error)) ||
        (e?.message && gatewayCodes.includes(e.message))
      ) {
        setGchatUnreachable(true);
        setChannelsError(null);
      } else if (e?.status === 422 && e?.validation) {
        const details = Object.entries(e.validation)
          .map(([k, msgs]) => `${k}: ${msgs.join(', ')}`)
          .join(' · ');
        setChannelsError(`Validation failed — ${details}`);
      } else {
        setChannelsError(e?.message || 'Failed to connect Google Chat.');
      }
    } finally {
      setSubmittingType(null);
    }
  };

  const handleAddEmailRecipient = () => {
    // The field takes one address or several, separated by commas or spaces.
    const drafted = splitEmails(emailForm.recipientDraft);
    if (drafted.length === 0) return;
    const refused = invalidEmails(drafted);
    if (refused.length > 0) {
      setRecipientError(
        t('EmailChannelInvalidRecipient', { addresses: refused.join(', ') })
      );
      return;
    }
    setRecipientError(null);
    setEmailForm((p) => ({
      ...p,
      recipients: [
        ...p.recipients,
        ...drafted.filter(
          (addr, i) =>
            !p.recipients.includes(addr) && drafted.indexOf(addr) === i
        ),
      ],
      recipientDraft: '',
    }));
  };

  const handleRemoveEmailRecipient = (addr: string) => {
    setEmailForm((p) => ({
      ...p,
      recipients: p.recipients.filter((r) => r !== addr),
    }));
  };

  const handleSubmitEmail = async () => {
    if (!activeShopSlug || !canConfigureChannels) return;
    if (
      !emailForm.resend_api_key.trim() ||
      !emailForm.sender_email.trim() ||
      emailForm.recipients.length === 0
    ) {
      setChannelsError(
        'Resend API key, sender email and at least one recipient are required.'
      );
      return;
    }
    // Nothing is saved while an address would be refused: the sender, a
    // listed recipient, or one still typed in the field.
    const refused = invalidEmails([
      ...emailForm.recipients,
      ...splitEmails(emailForm.recipientDraft),
    ]);
    if (refused.length > 0) {
      const message = t('EmailChannelInvalidRecipient', {
        addresses: refused.join(', '),
      });
      setRecipientError(message);
      setChannelsError(message);
      return;
    }
    if (!isValidEmail(emailForm.sender_email)) {
      setChannelsError(t('EmailChannelInvalidSender'));
      return;
    }
    setRecipientError(null);
    setSubmittingType('email');
    setChannelsError(null);
    try {
      await installManual(activeShopSlug, 'email', {
        resend_api_key: emailForm.resend_api_key,
        sender_email: emailForm.sender_email.trim(),
        sender_name: emailForm.sender_name.trim(),
        recipients: emailForm.recipients,
      });
      setEmailForm(emptyEmailForm);
      setOpenPanels((p) => ({ ...p, email: false }));
      showToast('success', 'Email notifications connected.');
      await loadChannels(activeShopSlug);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] email install failed', err);
      const e = err as {
        status?: number;
        message?: string;
        validation?: Record<string, string[]>;
      };
      if (e?.status === 422 && e?.validation) {
        const details = Object.entries(e.validation)
          .map(([k, msgs]) => `${k}: ${msgs.join(', ')}`)
          .join(' · ');
        setChannelsError(`Validation failed — ${details}`);
      } else {
        setChannelsError(
          e?.message || 'Failed to connect Email notifications.'
        );
      }
    } finally {
      setSubmittingType(null);
    }
  };

  const handleTest = async (channel: NotificationChannel) => {
    if (!activeShopSlug || !canConfigureChannels) return;
    setTestingId(channel.id);
    try {
      const res = await testChannel(activeShopSlug, channel.id);
      if (res.success) {
        showToast('success', `Test message sent via ${channel.channel_type}.`);
      } else {
        showToast(
          'error',
          res.error || `Test failed for ${channel.channel_type}.`
        );
      }
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] test failed', err);
      const e = err as { message?: string };
      showToast('error', e?.message || 'Test message failed.');
    } finally {
      setTestingId(null);
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!activeShopSlug || !confirmDisconnect.channel) return;
    setDisconnecting(true);
    try {
      await disconnectChannel(activeShopSlug, confirmDisconnect.channel.id);
      showToast(
        'info',
        `${confirmDisconnect.channel.channel_type} disconnected.`
      );
      setConfirmDisconnect({ open: false });
      await loadChannels(activeShopSlug);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[SettingsNotifications] disconnect failed', err);
      const e = err as { message?: string };
      setChannelsError(e?.message || 'Failed to disconnect.');
    } finally {
      setDisconnecting(false);
    }
  };

  // ---------- per-card connect renderers ----------
  // Microsoft Teams — connection-code linking. Before a code is generated we
  // show a "Generate connection code" button; once generated we show the code
  // (large + copyable), the server instructions, and a live countdown. The
  // async link is detected by `startTeamsPolling`, which flips the card to
  // "Connected".
  const renderTeamsConnect = (isConnectingTeams: boolean) => {
    if (!teamsLink) {
      return (
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            Add the ADUP bot to your Teams channel, then generate a one-time
            code to link it.
          </Typography>
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              variant='contained'
              onClick={handleGenerateTeamsCode}
              disabled={isConnectingTeams}
            >
              {isConnectingTeams
                ? 'Generating…'
                : 'Generate connection code'}
            </Button>
          </Stack>
        </Stack>
      );
    }

    const remainingMs = teamsLink.expiresAt - nowMs;
    return (
      <Stack spacing={2}>
        <Typography variant='body2' color='text.secondary'>
          {teamsLink.instructions ||
            'Add the ADUP bot to your Teams channel, @mention it, and paste this code.'}
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            component='code'
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: '0.15em',
              userSelect: 'all',
              wordBreak: 'break-all',
            }}
          >
            {teamsLink.code}
          </Typography>
          <Button
            size='small'
            variant='outlined'
            onClick={handleCopyTeamsCode}
          >
            {teamsCopied ? 'Copied' : 'Copy'}
          </Button>
        </Box>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='caption' color='text.secondary'>
            Waiting for you to paste this in Teams… we&apos;ll detect it
            automatically.
          </Typography>
          <Typography
            variant='caption'
            color={remainingMs <= 30_000 ? 'error' : 'text.secondary'}
          >
            Expires in {formatCountdown(remainingMs)}
          </Typography>
        </Stack>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            variant='text'
            onClick={handleGenerateTeamsCode}
            disabled={isConnectingTeams}
          >
            {isConnectingTeams ? 'Generating…' : 'Generate new code'}
          </Button>
        </Stack>
      </Stack>
    );
  };

  // Google Chat — manual connect with the single shared ADUP bot. Instructions
  // + a Space ID input + Connect. A 502 ("bot can't reach that space") shows
  // an inline hint to add the ADUP app first.
  const renderGChatForm = (isSubmittingGChat: boolean) => (
    <Stack spacing={2}>
      <Box component='ol' sx={{ pl: 2.5, m: 0 }}>
        <Typography component='li' variant='body2' color='text.secondary'>
          Add the ADUP app to your Google Chat space.
        </Typography>
        <Typography component='li' variant='body2' color='text.secondary'>
          Paste your Space ID below.
        </Typography>
      </Box>
      <TextField
        label='Space ID'
        placeholder='spaces/AAAAxxxxxxx'
        value={gchatForm.spaceId}
        onChange={(e) => {
          setGchatForm({ spaceId: e.target.value });
          if (gchatUnreachable) setGchatUnreachable(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isSubmittingGChat) {
            e.preventDefault();
            handleSubmitGChat();
          }
        }}
        fullWidth
        size='small'
        required
        disabled={isSubmittingGChat}
        autoComplete='off'
        helperText='Accepts a full "spaces/AAAA" ID or the bare ID.'
      />
      {gchatUnreachable && (
        <Alert severity='warning' onClose={() => setGchatUnreachable(false)}>
          Couldn&apos;t reach that space — make sure you added the ADUP app to
          it, then try again.
        </Alert>
      )}
      <Stack direction='row' spacing={1} justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSubmitGChat}
          disabled={isSubmittingGChat || !gchatForm.spaceId.trim()}
        >
          {isSubmittingGChat ? 'Connecting…' : 'Connect Google Chat'}
        </Button>
      </Stack>
    </Stack>
  );

  const renderEmailForm = (isSubmittingEmail: boolean) => (
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        Create a Resend account, verify a sender domain, and paste credentials
        below.
      </Typography>
      <TextField
        label='Resend API key'
        type='password'
        value={emailForm.resend_api_key}
        onChange={(e) =>
          setEmailForm((p) => ({ ...p, resend_api_key: e.target.value }))
        }
        fullWidth
        size='small'
        required
        disabled={isSubmittingEmail}
        autoComplete='off'
      />
      <TextField
        label='Sender email'
        type='email'
        value={emailForm.sender_email}
        onChange={(e) =>
          setEmailForm((p) => ({ ...p, sender_email: e.target.value }))
        }
        fullWidth
        size='small'
        required
        disabled={isSubmittingEmail}
      />
      <TextField
        label='Sender name'
        value={emailForm.sender_name}
        onChange={(e) =>
          setEmailForm((p) => ({ ...p, sender_name: e.target.value }))
        }
        fullWidth
        size='small'
        disabled={isSubmittingEmail}
      />
      <Box>
        <Typography variant='subtitle2' gutterBottom>
          Recipients
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          <TextField
            label='Add recipient email'
            value={emailForm.recipientDraft}
            onChange={(e) => {
              setRecipientError(null);
              setEmailForm((p) => ({
                ...p,
                recipientDraft: e.target.value,
              }));
            }}
            error={!!recipientError}
            helperText={recipientError || undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEmailRecipient();
              }
            }}
            fullWidth
            size='small'
            disabled={isSubmittingEmail}
          />
          <Button
            variant='outlined'
            onClick={handleAddEmailRecipient}
            disabled={isSubmittingEmail || !emailForm.recipientDraft.trim()}
          >
            Add
          </Button>
        </Stack>
        {emailForm.recipients.length > 0 && (
          <Stack
            direction='row'
            spacing={0.5}
            flexWrap='wrap'
            rowGap={0.5}
            sx={{ mt: 1 }}
          >
            {emailForm.recipients.map((addr) => (
              <Chip
                key={addr}
                label={addr}
                size='small'
                variant='outlined'
                onDelete={
                  isSubmittingEmail
                    ? undefined
                    : () => handleRemoveEmailRecipient(addr)
                }
              />
            ))}
          </Stack>
        )}
      </Box>
      <Stack direction='row' spacing={1} justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSubmitEmail}
          disabled={isSubmittingEmail}
        >
          {isSubmittingEmail ? 'Connecting…' : 'Connect Email'}
        </Button>
      </Stack>
    </Stack>
  );

  // ---------- per-platform card ----------
  const renderCard = (platform: PlatformDef) => {
    const channel = findChannel(channels, platform.type);
    const isConnecting = connectingType === platform.type;
    const isSubmittingPlatform = submittingType === platform.type;
    const panelOpen = openPanels[platform.type];

    return (
      <Card
        key={platform.type}
        variant='outlined'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={1}
          >
            <Typography variant='h6' component='h2'>
              {platform.label}
            </Typography>
            <StatusPill tone={channel ? 'success' : 'neutral'} withDot>
              {channel ? t('Connected') : t('Disconnected')}
            </StatusPill>
          </Stack>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 1, mb: 2 }}
          >
            {platform.description}
          </Typography>

          {channel && (
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                {platform.type === 'slack' && 'Workspace / channel'}
                {platform.type === 'teams' && 'Team / tenant'}
                {platform.type === 'gchat' && 'Workspace / space'}
                {platform.type === 'email' && 'Sender'}
              </Typography>
              <Typography variant='body2'>{externalLabel(channel)}</Typography>
            </Stack>
          )}

          {/* Microsoft Teams — connection-code linking renders inline (the
              "Generate connection code" button is the primary action; no
              accordion / paste-in form). */}
          {platform.type === 'teams' && canConfigureChannels && !channel && (
            <Box sx={{ mt: 1 }}>{renderTeamsConnect(isConnecting)}</Box>
          )}

          {/* Manual install accordion (GChat / Email). */}
          {platform.manual &&
            platform.type !== 'teams' &&
            canConfigureChannels &&
            !channel && (
              <Accordion
                expanded={panelOpen}
                onChange={(_, exp) =>
                  setOpenPanels((p) => ({ ...p, [platform.type]: exp }))
                }
                disableGutters
                elevation={0}
                square
                sx={{
                  mt: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Box
                      component='span'
                      sx={{ fontSize: 18, lineHeight: 1 }}
                    >
                      ▾
                    </Box>
                  }
                >
                  <Typography variant='subtitle2'>
                    Setup instructions and paste-in form
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {platform.type === 'gchat' &&
                    renderGChatForm(isSubmittingPlatform)}
                  {platform.type === 'email' &&
                    renderEmailForm(isSubmittingPlatform)}
                </AccordionDetails>
              </Accordion>
            )}
        </CardContent>
        <CardActions>
          {channel ? (
            <Stack
              direction='row'
              spacing={1}
              sx={{ width: '100%' }}
              justifyContent='flex-end'
            >
              <RoleGate capability='notifications.configure'>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => handleTest(channel)}
                  disabled={testingId === channel.id}
                >
                  {testingId === channel.id ? 'Sending…' : 'Send test'}
                </Button>
              </RoleGate>
              <RoleGate capability='notifications.configure'>
                <Button
                  size='small'
                  color='error'
                  variant='outlined'
                  onClick={() =>
                    setConfirmDisconnect({ open: true, channel })
                  }
                >
                  Disconnect
                </Button>
              </RoleGate>
            </Stack>
          ) : (
            <Stack
              direction='row'
              spacing={1}
              sx={{ width: '100%' }}
              justifyContent='flex-end'
            >
              {!platform.manual && (
                <Button
                  size='small'
                  variant='text'
                  onClick={() =>
                    activeShopSlug && loadChannels(activeShopSlug)
                  }
                  disabled={channelsLoading || !activeShopSlug}
                >
                  Refresh
                </Button>
              )}
              {/* Teams drives its own inline "Generate connection code" action
                  above, so the generic Connect button is hidden for it. */}
              {platform.type === 'teams' ? (
                !canConfigureChannels && (
                  <Typography variant='caption' color='text.secondary'>
                    Read-only role — ask an owner to connect.
                  </Typography>
                )
              ) : (
                <RoleGate
                  capability='notifications.configure'
                  fallback={
                    <Typography variant='caption' color='text.secondary'>
                      Read-only role — ask an owner to connect.
                    </Typography>
                  }
                >
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => handleConnectClick(platform)}
                    disabled={isConnecting || !activeShopSlug}
                  >
                    {isConnecting ? 'Starting…' : 'Connect'}
                  </Button>
                </RoleGate>
              )}
            </Stack>
          )}
        </CardActions>
      </Card>
    );
  };

  // ---------- render ----------
  return (
    <SubPageInnerContentHolder
      title={t('NotificationSettings')}
      backBtnLink={isWhiteLabel ? undefined : HOME_ROUTE}
    >
      <Box>
        {/* ---------- Section: Approval channels ---------- */}
        <Box sx={{ mb: 4 }}>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant='h6'>Approval channels</Typography>
            <Typography variant='body2' color='text.secondary'>
              Configure where ADUP sends proposal alerts. One per platform per
              shop.
            </Typography>
          </Stack>

          {channelsError && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
              onClose={() => setChannelsError(null)}
            >
              {channelsError}
            </Alert>
          )}

          {!activeShopSlug ? (
            <Alert severity='info'>
              Select a shop to configure notifications.
            </Alert>
          ) : !hasAgencyToken ? (
            <Alert severity='info'>
              Sign in to the agency portal to manage approval channels for
              this shop.
            </Alert>
          ) : (
            <>
              {!canConfigureChannels && (
                <Alert severity='info' sx={{ mb: 2 }}>
                  You don&apos;t have permission to modify notification
                  channels.
                </Alert>
              )}

              {channelsLoading && (
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <CircularProgress size={20} />
                  <Typography>Loading notification channels…</Typography>
                </Stack>
              )}

              {!channelsLoading && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, 1fr)',
                    },
                    gap: 2,
                  }}
                >
                  {PLATFORMS.map(renderCard)}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ---------- Section: Email preferences (legacy, preserved) ---------- */}
        <Box>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant='h6'>Email preferences</Typography>
          </Stack>
          {loading === 'pending' && <LoadingSpinner />}
          {loading === 'error' && (
            <NoData
              title={t('ErrorFetchingData')}
              subTitle={t('SomethingWentWrong-msg')}
            />
          )}
          {loading === 'success' && (
            <form
              id='notification-settings-form'
              onSubmit={onSubmitHandler}
              className='normalForm'
            >
              <div className='normalForm__top'>
                <h2>{t('CustomMessage')}</h2>
                <hr />
                <div className='normalForm__container1'>
                  <div className='normalForm__container1--left'>
                    <ToggleSwitch
                      isOn={isShowCustomMessage}
                      title={t('ShowCustomMessage')}
                      onClick={setIsShowCustomMessage}
                      classes='margin-top-2rem'
                      id='notification-settings-is-custom-message'
                    />
                  </div>
                  <div className='normalForm__container1--right'>
                    <NormalInput
                      id='notification-settings-custom-message'
                      value={customMessage}
                      changeListeners={[
                        (e) => setCustomMessage(e.target.value),
                      ]}
                      focusListeners={[]}
                      placeholder={t('CustomMessage')}
                      classes='lg:w-[13rem]'
                      required={isShowCustomMessage}
                      pattern='^.{1,100}$'
                      minlength={1}
                      maxlength={100}
                      title={t('CustomMessage-validation')}
                    />
                  </div>
                </div>
              </div>

              <div className='normalForm__bottom flex-start'>
                <MainSquareButton
                  value={t('UpdateSettings')}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  type='submit'
                  form='notification-settings-form'
                />
              </div>
            </form>
          )}
        </Box>

        {/* ---------- Disconnect confirm popup (shared legacy Popup) ---------- */}
        <Popup
          styles={{ backgroundColor: 'var(--sidebar-background-color)' }}
          trigger={confirmDisconnect.open}
          setTrigger={() =>
            !disconnecting && setConfirmDisconnect({ open: false })
          }
        >
          <div className='teamPopup'>
            <h3 className='teamPopup__title'>
              {t('DisconnectChannelTitle')}
            </h3>
            <p className='teamPopup__confirmBody'>
              {confirmDisconnect.channel ? (
                <>
                  {t('DisconnectChannelBody', {
                    channel: confirmDisconnect.channel.channel_type,
                  })}{' '}
                  <strong>{activeShopSlug}</strong>.
                </>
              ) : (
                t('DisconnectChannelBodyGeneric')
              )}
            </p>
            <div className='teamPopup__actions'>
              <MainSquareButton
                type='button'
                value={t('Cancel')}
                buttonType='white'
                normal={true}
                disabled={disconnecting}
                onClick={(e) => {
                  e.preventDefault();
                  setConfirmDisconnect({ open: false });
                }}
              />
              <MainSquareButton
                type='button'
                value={t('Disconnect')}
                buttonType='danger'
                normal={true}
                loading={disconnecting}
                disabled={disconnecting}
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmDisconnect();
                }}
              />
            </div>
          </div>
        </Popup>
      </Box>
    </SubPageInnerContentHolder>
  );
};

export default SettingsNotifications;
